import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs';
import { ToastService } from 'src/app/core/services/toast.service';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import {
  IApiResponseGroupSignedOffDocuments,
  ISignOffResponse,
  ISignOffNotification,
  IDocumentsToSignOffResponse,
} from '../models/sign-off.model';
import { SignOffApiService } from '../services/sign-off-api.service';
import {
  CheckForDocumentsToBeSignedOff,
  LoadDocumentsToBeSignedOff,
  LoadSignedOffDocumentDetails,
  LoadSignedOffDocumentsList,
  PrintSignedOffDocumentPdf,
  RejectDocumentAsSeafarer,
  SignOffDocumentAsAdmin,
  SignOffDocumentAsSeafarer,
  ClearCurrentSignOff,
  ReopenDocument,
  CloseDocument,
  RemindSeafarer,
} from './sign-off.actions';

export interface SignOffStateModel {
  documentsToBeSignedOffAdmin: ISignOffResponse[];
  documentsToBeSignedOffSeafarer: ISignOffResponse[];
  signedOffDocumentsResponse: ISignOffResponse[];
  signedOffDocumentsTotalPages: number;
  currentSignOffDocument: ISignOffResponse | undefined;
  currentSignOffDocumentPdf: Blob | undefined;
  signOffNotification: ISignOffNotification | undefined;
}

const SIGN_OFF_STATE_TOKEN = new StateToken<SignOffStateModel>('signOffs');

@State<SignOffStateModel>({
  name: SIGN_OFF_STATE_TOKEN,
  defaults: {
    documentsToBeSignedOffAdmin: [],
    documentsToBeSignedOffSeafarer: [],
    signedOffDocumentsResponse: [],
    signedOffDocumentsTotalPages: 0,
    currentSignOffDocument: undefined,
    currentSignOffDocumentPdf: undefined,
    signOffNotification: undefined,
  },
})
@Injectable()
export class SignOffState {
  constructor(
    private service: SignOffApiService,
    private toastService: ToastService
  ) {}

  @Action(CheckForDocumentsToBeSignedOff, { cancelUncompleted: true })
  checkForDocumentsToBeSignedOff({ getState, setState }: StateContext<SignOffStateModel>) {
    return this.service.getSignOffNotification().pipe(
      tap((response: ISignOffNotification) => {
        const state = getState();
        setState({
          ...state,
          signOffNotification: response,
        });
      })
    );
  }

  @Action(LoadDocumentsToBeSignedOff, { cancelUncompleted: true })
  getDocumentsToBeSignedOff({ getState, setState }: StateContext<SignOffStateModel>) {
    return this.service.getDocumentsToBeSignedOff().pipe(
      tap((response: IDocumentsToSignOffResponse) => {
        const state = getState();
        setState({
          ...state,
          documentsToBeSignedOffAdmin: response.documentsToSignOffByAdmin,
          documentsToBeSignedOffSeafarer: response.documentsToSignOffBySeafarer,
        });
      })
    );
  }

  @Action(LoadSignedOffDocumentsList, { cancelUncompleted: true })
  getSignedOffDocuments(
    { getState, setState }: StateContext<SignOffStateModel>,
    { filter }: LoadSignedOffDocumentsList
  ) {
    return this.service.getSignedOffDocuments(filter).pipe(
      tap((response: IApiResponseGroupSignedOffDocuments) => {
        const state = getState();
        setState({
          ...state,
          signedOffDocumentsResponse: response.data,
          signedOffDocumentsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadSignedOffDocumentDetails, { cancelUncompleted: true })
  getSignedOffDocumentById(
    { getState, setState }: StateContext<SignOffStateModel>,
    { id }: LoadSignedOffDocumentDetails
  ) {
    return this.service.getSignedOffDocumentById(id).pipe(
      tap((response: ISignOffResponse) => {
        const state = getState();
        setState({
          ...state,
          currentSignOffDocument: response,
        });
      })
    );
  }

  @Action(PrintSignedOffDocumentPdf, { cancelUncompleted: true })
  printSignedOffDocumentPdf(
    { getState, setState }: StateContext<SignOffStateModel>,
    { id }: PrintSignedOffDocumentPdf
  ) {
    return this.service.printSignedOffDocumentById(id).pipe(
      tap((response: Blob) => {
        const state = getState();
        setState({
          ...state,
          currentSignOffDocumentPdf: response,
        });
      })
    );
  }

  @Action(RejectDocumentAsSeafarer, { cancelUncompleted: true })
  @OnSuccess({ message: 'workAndRestDocumentRejected' })
  rejectDocumentAsSeafarer(
    { getState, setState }: StateContext<SignOffStateModel>,
    { id, comment }: RejectDocumentAsSeafarer
  ) {
    return this.service.rejectDocumentAsSeafarer(id, comment).pipe(
      tap((response: ISignOffResponse) => {
        const state = getState();
        const currentDoc = state.currentSignOffDocument;
        const shouldUpdateCurrent = currentDoc && currentDoc.id === response.id;

        setState(
          patch({
            documentsToBeSignedOffSeafarer: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            ...(shouldUpdateCurrent && { currentSignOffDocument: response }),
          })
        );
      })
    );
  }

  @Action(SignOffDocumentAsAdmin, { cancelUncompleted: true })
  @OnSuccess({ message: 'workAndRestDocumentSignedAndSent' })
  signOffDocumentAsAdmin(
    { getState, setState }: StateContext<SignOffStateModel>,
    { request }: SignOffDocumentAsAdmin
  ) {
    return this.service.signOffDocumentAsAdmin(request).pipe(
      tap((response: ISignOffResponse) => {
        const state = getState();
        const currentDoc = state.currentSignOffDocument;
        const shouldUpdateCurrent = currentDoc && currentDoc.id === response.id;

        setState(
          patch({
            documentsToBeSignedOffAdmin: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            ...(shouldUpdateCurrent && { currentSignOffDocument: response }),
          })
        );
      })
    );
  }

  @Action(SignOffDocumentAsSeafarer, { cancelUncompleted: true })
  @OnSuccess({ message: 'workAndRestDocumentSignedAndSent' })
  signOffDocumentAsSeafarer(
    { getState, setState }: StateContext<SignOffStateModel>,
    { request }: SignOffDocumentAsSeafarer
  ) {
    return this.service.signOffDocumentAsSeafarer(request).pipe(
      tap((response: ISignOffResponse) => {
        const state = getState();
        const currentDoc = state.currentSignOffDocument;
        const shouldUpdateCurrent = currentDoc && currentDoc.id === response.id;

        setState(
          patch({
            documentsToBeSignedOffSeafarer: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            ...(shouldUpdateCurrent && { currentSignOffDocument: response }),
          })
        );
      })
    );
  }

  @Action(ReopenDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'workAndRestDocumentReopened' })
  reopenDocument(
    { getState, setState }: StateContext<SignOffStateModel>,
    { plannedActivitySignOffPeriodId, comment }: ReopenDocument
  ) {
    return this.service.reopenDocument(plannedActivitySignOffPeriodId, comment).pipe(
      tap((response: ISignOffResponse) => {
        const state = getState();
        const currentDoc = state.currentSignOffDocument;
        const shouldUpdateCurrent = currentDoc && currentDoc.id === response.id;

        setState(
          patch({
            signedOffDocumentsResponse: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            documentsToBeSignedOffAdmin: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            documentsToBeSignedOffSeafarer: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            ...(shouldUpdateCurrent && { currentSignOffDocument: response }),
          })
        );
      })
    );
  }

  @Action(CloseDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'workAndRestDocumentClosed' })
  closeDocument(
    { getState, setState }: StateContext<SignOffStateModel>,
    { plannedActivitySignOffPeriodId, comment }: CloseDocument
  ) {
    return this.service.closeDocument(plannedActivitySignOffPeriodId, comment).pipe(
      tap((response: ISignOffResponse) => {
        const state = getState();
        const currentDoc = state.currentSignOffDocument;
        const shouldUpdateCurrent = currentDoc && currentDoc.id === response.id;

        setState(
          patch({
            signedOffDocumentsResponse: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            documentsToBeSignedOffAdmin: removeItem<ISignOffResponse>(
              (toRemove) => toRemove?.id === response.id
            ),
            documentsToBeSignedOffSeafarer: removeItem<ISignOffResponse>(
              (toRemove) => toRemove?.id === response.id
            ),
            ...(shouldUpdateCurrent && { currentSignOffDocument: response }),
          })
        );
      })
    );
  }

  @Action(RemindSeafarer, { cancelUncompleted: true })
  @OnSuccess({ message: 'workAndRestSeafarerReminderSent' })
  remindSeafarer(
    { getState, setState }: StateContext<SignOffStateModel>,
    { plannedActivitySignOffPeriodId, message }: RemindSeafarer
  ) {
    return this.service.remindSeafarer(plannedActivitySignOffPeriodId, message).pipe(
      tap((response: ISignOffResponse) => {
        const state = getState();
        const currentDoc = state.currentSignOffDocument;
        const shouldUpdateCurrent = currentDoc && currentDoc.id === response.id;

        setState(
          patch({
            signedOffDocumentsResponse: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            documentsToBeSignedOffAdmin: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            documentsToBeSignedOffSeafarer: updateItem<ISignOffResponse>(
              (toUpdate) => toUpdate?.id === response.id,
              response
            ),
            ...(shouldUpdateCurrent && { currentSignOffDocument: response }),
          })
        );
      })
    );
  }

  @Action(ClearCurrentSignOff)
  clearCurrentSignOff(ctx: StateContext<SignOffStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentSignOffDocument: undefined,
    });
  }

  @Selector()
  static getDocumentsToBeSignedOff(state: SignOffStateModel): ISignOffResponse[] {
    return [...state.documentsToBeSignedOffSeafarer, ...state.documentsToBeSignedOffAdmin];
  }

  @Selector()
  static getDocumentsToBeSignedOffSeafarer(state: SignOffStateModel): ISignOffResponse[] {
    return [...state.documentsToBeSignedOffSeafarer];
  }

  @Selector()
  static getSignedOffDocuments(state: SignOffStateModel): ISignOffResponse[] {
    return state.signedOffDocumentsResponse;
  }

  @Selector()
  static getSignedOffDocumentsTotalPages(state: SignOffStateModel): number {
    return state.signedOffDocumentsTotalPages;
  }

  @Selector()
  static getSignedOffDocumentDetails(state: SignOffStateModel) {
    return state.currentSignOffDocument;
  }

  @Selector()
  static getSignOffById(state: SignOffStateModel) {
    return state.currentSignOffDocument;
  }

  @Selector()
  static getSignOffNotification(state: SignOffStateModel): ISignOffNotification | undefined {
    return state.signOffNotification;
  }

  @Selector()
  static getCurrentSignOffDocumentPdf(state: SignOffStateModel): Blob | undefined {
    return state.currentSignOffDocumentPdf;
  }
}
